// ==================== ROAD FATALITY TRACKER ====================
// Interactive visualization of fatal road accidents in Jamaica
// Data sourced from news articles and community submissions

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  AlertTriangle, MapPin, Calendar, Filter, TrendingUp, Users, Car,
  Bike, PersonStanding, Bus, Truck, ChevronDown, ChevronUp, X, Plus,
  ExternalLink, Clock, Shield, Info, Send, Loader2, RefreshCw,
  BarChart3, PieChart, List, Map as MapIcon, FileText, Heart,
  AlertCircle, CheckCircle, Search, Download, Share2, Skull,
  Baby, User, UserCircle, ArrowLeft, Eye, Newspaper
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { roadFatalities } from '../lib/supabase';
import { PARISHES } from '../lib/constants';
import { PARISH_COORDS } from '../lib/trafficService';

// Jamaica GeoJSON boundaries (simplified for performance)
const JAMAICA_BOUNDS = [[17.7, -78.4], [18.6, -76.2]];
const JAMAICA_CENTER = [18.1096, -77.2975];

// Vehicle type icons and labels
const VEHICLE_TYPES = {
  car: { icon: Car, label: 'Car', color: 'blue' },
  motorcycle: { icon: Bike, label: 'Motorcycle', color: 'orange' },
  bicycle: { icon: Bike, label: 'Bicycle', color: 'green' },
  pedestrian: { icon: PersonStanding, label: 'Pedestrian', color: 'purple' },
  bus: { icon: Bus, label: 'Bus', color: 'yellow' },
  truck: { icon: Truck, label: 'Truck', color: 'gray' },
  minibus: { icon: Bus, label: 'Minibus', color: 'teal' }
};

// Age group labels
const AGE_GROUPS = {
  child: { label: 'Children (0-12)', icon: Baby, color: 'pink' },
  teen: { label: 'Teens (13-19)', icon: User, color: 'purple' },
  young_adult: { label: 'Young Adults (20-39)', icon: UserCircle, color: 'blue' },
  adult: { label: 'Adults (40-59)', icon: UserCircle, color: 'emerald' },
  senior: { label: 'Seniors (60+)', icon: UserCircle, color: 'amber' },
  unknown: { label: 'Unknown', icon: User, color: 'gray' }
};

// Victim roles
const VICTIM_ROLES = {
  driver: 'Driver',
  passenger: 'Passenger',
  pedestrian: 'Pedestrian',
  cyclist: 'Cyclist',
  pillion: 'Pillion Rider',
  unknown: 'Unknown'
};

// Generate years from 2015 to current (for historical data), newest first
const CURRENT_YEAR = Math.max(2026, new Date().getFullYear());
const YEARS = Array.from({ length: CURRENT_YEAR - 2014 }, (_, i) => CURRENT_YEAR - i);

// Format date in Jamaica timezone (UTC-5, no DST)
const formatJamaicaDate = (dateStr, options = {}) => {
  if (!dateStr) return '';
  // Extract just the date part (YYYY-MM-DD) to avoid timezone issues
  const datePart = String(dateStr).split('T')[0];
  const date = new Date(datePart + 'T12:00:00');
  const defaultOptions = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
  return date.toLocaleDateString('en-JM', { ...defaultOptions, ...options });
};

const formatJamaicaDateShort = (dateStr) => {
  if (!dateStr) return '';
  const datePart = String(dateStr).split('T')[0];
  const date = new Date(datePart + 'T12:00:00');
  return date.toLocaleDateString('en-JM', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Format time to 12-hour format (Jamaica style)
const formatTime = (timeStr) => {
  if (!timeStr) return '';
  const [hours, minutes] = String(timeStr).split(':');
  const h = parseInt(hours);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

// ==================== MAIN COMPONENT ====================
const RoadFatalityTracker = ({ onBack, showToast, setPage }) => {
  const { user, isAuthenticated } = useAuth();
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);

  // State
  const [mapContainerReady, setMapContainerReady] = useState(false);
  const [mapInstanceReady, setMapInstanceReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [accidents, setAccidents] = useState([]);
  const [stats, setStats] = useState(null);
  const [view, setView] = useState('map'); // map, list, stats, submit
  const [selectedAccident, setSelectedAccident] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  // Filters - default to current year (at least 2026)
  const [filters, setFilters] = useState({
    year: CURRENT_YEAR,
    parish: '',
    vehicleType: '',
    ageGroup: ''
  });

  // Submission form
  const [submitting, setSubmitting] = useState(false);
  const [submitForm, setSubmitForm] = useState({
    accident_date: '',
    accident_time: '',
    parish: '',
    city: '',
    location_description: '',
    road_name: '',
    accident_type: 'collision',
    vehicles_involved: [],
    fatality_count: 1,
    injury_count: 0,
    source_url: '',
    source_name: '',
    summary: '',
    victims: [{ age: '', gender: '', role: 'unknown', parish_of_origin: '' }]
  });

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [accidentsRes, statsRes] = await Promise.all([
        roadFatalities.getAll(filters),
        roadFatalities.getStats(filters)
      ]);

      if (accidentsRes.data) setAccidents(accidentsRes.data);
      if (statsRes.data) setStats(statsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      showToast?.('Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  }, [filters, showToast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Callback ref for map container - triggers state update when DOM is ready
  const mapContainerRef = useCallback((node) => {
    if (node !== null) {
      mapRef.current = node;
      setMapContainerReady(true);
    }
  }, []);

  // Initialize map when container is ready and view is map
  useEffect(() => {
    if (view !== 'map' || !mapContainerReady || !mapRef.current || mapInstanceRef.current) return;

    // Function to initialize the map
    const initMap = () => {
      const L = window.L;
      if (!L || !mapRef.current || mapInstanceRef.current) return false;

      // Create map with performance optimizations
      const map = L.map(mapRef.current, {
        center: JAMAICA_CENTER,
        zoom: 9,
        minZoom: 8,
        maxZoom: 18,
        maxBounds: JAMAICA_BOUNDS,
        maxBoundsViscosity: 1.0,
        preferCanvas: true,  // Better performance for markers
        zoomControl: true,
        attributionControl: false  // Add manually for cleaner UI
      });

      // Use CartoDB tiles (faster CDN) with optimized loading
      L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
        subdomains: 'abcd',
        maxZoom: 19,
        updateWhenIdle: true,
        updateWhenZooming: false,
        keepBuffer: 4
      }).addTo(map);

      // Add attribution in corner
      L.control.attribution({ position: 'bottomright', prefix: false }).addTo(map);

      mapInstanceRef.current = map;
      setMapInstanceReady(true);

      // Invalidate size after a short delay to ensure proper rendering
      setTimeout(() => map.invalidateSize(), 100);
      return true;
    };

    // Try to init immediately, or poll until Leaflet is available
    if (!initMap()) {
      const checkLeaflet = setInterval(() => {
        if (initMap()) {
          clearInterval(checkLeaflet);
        }
      }, 100);

      // Clear interval after 10 seconds to prevent infinite polling
      setTimeout(() => clearInterval(checkLeaflet), 10000);

      return () => clearInterval(checkLeaflet);
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        setMapInstanceReady(false);
      }
    };
  }, [view, mapContainerReady]);

  // Update map markers when map is ready or data changes
  useEffect(() => {
    if (!mapInstanceRef.current || !mapInstanceReady || view !== 'map') return;

    const L = window.L;
    if (!L) return;

    // Clear existing markers
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    // Group accidents by location for clustering
    const locationGroups = {};
    accidents.forEach(accident => {
      const key = accident.latitude && accident.longitude
        ? `${accident.latitude.toFixed(3)},${accident.longitude.toFixed(3)}`
        : accident.parish;

      if (!locationGroups[key]) {
        locationGroups[key] = {
          accidents: [],
          lat: accident.latitude || PARISH_COORDS[accident.parish]?.lat,
          lng: accident.longitude || PARISH_COORDS[accident.parish]?.lng,
          parish: accident.parish,
          totalFatalities: 0
        };
      }
      locationGroups[key].accidents.push(accident);
      locationGroups[key].totalFatalities += accident.fatality_count || 0;
    });

    // Create markers with pulsing effect based on fatality count
    Object.values(locationGroups).forEach(group => {
      if (!group.lat || !group.lng) return;

      const size = Math.min(50, 20 + group.totalFatalities * 3);
      const pulseSpeed = Math.max(1, 3 - group.totalFatalities * 0.2);

      // Create pulsing marker
      const pulseIcon = L.divIcon({
        className: 'pulse-marker',
        html: `
          <div class="pulse-marker-inner" style="
            width: ${size}px;
            height: ${size}px;
            background: radial-gradient(circle, rgba(239, 68, 68, 0.9) 0%, rgba(239, 68, 68, 0.4) 50%, transparent 70%);
            border-radius: 50%;
            animation: pulse ${pulseSpeed}s ease-out infinite;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-weight: bold;
            font-size: ${Math.max(10, size / 3)}px;
            text-shadow: 0 0 3px rgba(0,0,0,0.5);
          ">
            ${group.totalFatalities}
          </div>
        `,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2]
      });

      const marker = L.marker([group.lat, group.lng], { icon: pulseIcon })
        .addTo(mapInstanceRef.current);

      // Popup content
      const popupContent = `
        <div class="p-2 max-w-xs">
          <h3 class="font-bold text-red-600 mb-1">${group.parish}</h3>
          <p class="text-sm text-gray-600 mb-2">${group.totalFatalities} fatalities in ${group.accidents.length} incident(s)</p>
          <div class="space-y-1 max-h-32 overflow-y-auto">
            ${group.accidents.slice(0, 5).map(a => `
              <div class="text-xs border-t pt-1">
                <span class="font-medium">${formatJamaicaDateShort(a.accident_date)}</span>
                - ${a.fatality_count} ${a.fatality_count === 1 ? 'death' : 'deaths'}
                ${a.location_description ? `<br><span class="text-gray-500">${a.location_description.substring(0, 50)}...</span>` : ''}
              </div>
            `).join('')}
            ${group.accidents.length > 5 ? `<div class="text-xs text-gray-500 mt-1">+${group.accidents.length - 5} more...</div>` : ''}
          </div>
        </div>
      `;

      marker.bindPopup(popupContent);
      marker.on('click', () => {
        if (group.accidents.length === 1) {
          setSelectedAccident(group.accidents[0]);
        }
      });

      markersRef.current.push(marker);
    });
  }, [accidents, view, mapInstanceReady]);

  // Parish heatmap data
  const parishHeatmap = stats?.byParish || {};
  const maxParishFatalities = Math.max(...Object.values(parishHeatmap), 1);

  // Chart for trends
  useEffect(() => {
    if (view !== 'stats' || !chartRef.current || !stats?.byMonth) return;

    const Chart = window.Chart;
    if (!Chart) return;

    // Destroy existing chart
    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy();
    }

    const months = Object.keys(stats.byMonth).sort();
    const values = months.map(m => stats.byMonth[m]);

    chartInstanceRef.current = new Chart(chartRef.current, {
      type: 'line',
      data: {
        labels: months.map(m => {
          const [year, month] = m.split('-');
          return new Date(year, month - 1).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
        }),
        datasets: [{
          label: 'Fatalities',
          data: values,
          borderColor: 'rgb(239, 68, 68)',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          fill: true,
          tension: 0.3
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) => `${ctx.raw} fatalities`
            }
          }
        },
        scales: {
          y: { beginAtZero: true, ticks: { stepSize: 1 } }
        }
      }
    });

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  }, [view, stats]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      showToast?.('Please log in to submit a report', 'error');
      return;
    }

    if (!submitForm.source_url) {
      showToast?.('Please provide a source URL for verification', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const { error } = await roadFatalities.create(submitForm, user?.id);
      if (error) throw error;

      showToast?.('Report submitted for review. Thank you!', 'success');
      setView('map');
      setSubmitForm({
        accident_date: '',
        accident_time: '',
        parish: '',
        city: '',
        location_description: '',
        road_name: '',
        accident_type: 'collision',
        vehicles_involved: [],
        fatality_count: 1,
        injury_count: 0,
        source_url: '',
        source_name: '',
        summary: '',
        victims: [{ age: '', gender: '', role: 'unknown', parish_of_origin: '' }]
      });
    } catch (error) {
      console.error('Submit error:', error);
      showToast?.('Failed to submit report', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Add victim to form
  const addVictim = () => {
    setSubmitForm(prev => ({
      ...prev,
      victims: [...prev.victims, { age: '', gender: '', role: 'unknown', parish_of_origin: '' }]
    }));
  };

  // Remove victim from form
  const removeVictim = (index) => {
    if (submitForm.victims.length <= 1) return;
    setSubmitForm(prev => ({
      ...prev,
      victims: prev.victims.filter((_, i) => i !== index)
    }));
  };

  // Update victim in form
  const updateVictim = (index, field, value) => {
    setSubmitForm(prev => ({
      ...prev,
      victims: prev.victims.map((v, i) => i === index ? { ...v, [field]: value } : v)
    }));
  };

  // Toggle vehicle type in form
  const toggleVehicle = (type) => {
    setSubmitForm(prev => ({
      ...prev,
      vehicles_involved: prev.vehicles_involved.includes(type)
        ? prev.vehicles_involved.filter(v => v !== type)
        : [...prev.vehicles_involved, type]
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* CSS for pulsing animation */}
      <style>{`
        @keyframes pulse {
          0% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.3); opacity: 0.7; }
          100% { transform: scale(1); opacity: 1; }
        }
        .pulse-marker-inner {
          cursor: pointer;
          transition: transform 0.2s;
        }
        .pulse-marker-inner:hover {
          transform: scale(1.2) !important;
          animation-play-state: paused;
        }
        .leaflet-popup-content-wrapper {
          border-radius: 12px;
        }
        .leaflet-popup-content {
          margin: 8px 12px;
        }
        /* Ensure map stays below sticky header */
        .leaflet-container {
          z-index: 1 !important;
        }
        .leaflet-pane {
          z-index: 1 !important;
        }
        .leaflet-top,
        .leaflet-bottom {
          z-index: 10 !important;
        }
      `}</style>

      {/* Header */}
      <div className="bg-gradient-to-r from-red-800 via-red-900 to-red-950 text-white rounded-2xl shadow-xl overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Back</span>
            </button>
            <div className="flex items-center gap-2">
              <button
                onClick={loadData}
                disabled={loading}
                className="p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
                title="Refresh data"
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 bg-white/20 rounded-xl">
              <Skull className="w-8 h-8" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-2xl md:text-3xl font-bold">Road Fatality Map</h1>
                <span className="px-3 py-1 bg-white/20 rounded-full text-lg font-bold">{filters.year || 'All Years'}</span>
              </div>
              <p className="text-red-100 text-sm md:text-base">Honouring lives lost. Driving change.</p>
            </div>
          </div>

          {/* Quick Stats */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
              <div className="bg-white/10 rounded-xl p-3 backdrop-blur-sm">
                <div className="text-2xl md:text-3xl font-bold">{stats.totalFatalities}</div>
                <div className="text-xs text-red-100">Total Fatalities</div>
              </div>
              <div className="bg-white/10 rounded-xl p-3 backdrop-blur-sm">
                <div className="text-2xl md:text-3xl font-bold">{stats.totalAccidents}</div>
                <div className="text-xs text-red-100">Fatal Incidents</div>
              </div>
              <div className="bg-white/10 rounded-xl p-3 backdrop-blur-sm">
                <div className="text-2xl md:text-3xl font-bold">{stats.byVictimAge?.child || 0}</div>
                <div className="text-xs text-red-100">Children Lost</div>
              </div>
              <div className="bg-white/10 rounded-xl p-3 backdrop-blur-sm">
                <div className="text-2xl md:text-3xl font-bold">{stats.byVictimRole?.pedestrian || 0}</div>
                <div className="text-xs text-red-100">Pedestrians</div>
              </div>
            </div>
          )}

          {/* Disclaimer */}
          <div className="mt-4 bg-yellow-500/20 border border-yellow-400/30 rounded-lg px-3 py-2 flex items-start gap-2">
            <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-yellow-100">
              <strong>Disclaimer:</strong> This is a work in progress. Data is continuously updated and verified.
              Figures shown may not represent official statistics. Sources are tracked for all entries.
            </p>
          </div>
        </div>
      </div>

      {/* View Tabs & Filters */}
      <div className="bg-white border-b sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between py-3">
            {/* View Tabs */}
            <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
              {[
                { id: 'map', icon: MapIcon, label: 'Map' },
                { id: 'list', icon: List, label: 'List' },
                { id: 'stats', icon: BarChart3, label: 'Stats' },
                { id: 'submit', icon: Plus, label: 'Submit' }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setView(tab.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    view === tab.id
                      ? 'bg-white text-red-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              ))}
            </div>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-colors ${
                showFilters ? 'bg-red-50 border-red-200 text-red-600' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Filter className="w-4 h-4" />
              <span className="hidden sm:inline">Filters</span>
              {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="pb-4 grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Year</label>
                <select
                  value={filters.year}
                  onChange={(e) => setFilters(f => ({ ...f, year: e.target.value ? parseInt(e.target.value) : '' }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                >
                  <option value="">All Years</option>
                  {YEARS.map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Parish</label>
                <select
                  value={filters.parish}
                  onChange={(e) => setFilters(f => ({ ...f, parish: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                >
                  <option value="">All Parishes</option>
                  {PARISHES.map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Vehicle Type</label>
                <select
                  value={filters.vehicleType}
                  onChange={(e) => setFilters(f => ({ ...f, vehicleType: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                >
                  <option value="">All Types</option>
                  {Object.entries(VEHICLE_TYPES).map(([key, { label }]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Victim Age</label>
                <select
                  value={filters.ageGroup}
                  onChange={(e) => setFilters(f => ({ ...f, ageGroup: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                >
                  <option value="">All Ages</option>
                  {Object.entries(AGE_GROUPS).map(([key, { label }]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-red-600" />
          </div>
        ) : (
          <>
            {/* Map View */}
            {view === 'map' && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Map */}
                <div className="lg:col-span-2">
                  <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                    <div
                      ref={mapContainerRef}
                      className="h-[400px] md:h-[500px] lg:h-[600px] w-full"
                    />
                  </div>
                </div>

                {/* Sidebar */}
                <div className="space-y-4">
                  {/* Parish Rankings */}
                  <div className="bg-white rounded-2xl shadow-lg p-4">
                    <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-red-600" />
                      Worst Affected Parishes
                    </h3>
                    <div className="space-y-2">
                      {Object.entries(parishHeatmap)
                        .sort(([, a], [, b]) => b - a)
                        .slice(0, 7)
                        .map(([parish, count], index) => (
                          <div key={parish} className="flex items-center gap-3">
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                              index === 0 ? 'bg-red-600 text-white' :
                              index === 1 ? 'bg-red-500 text-white' :
                              index === 2 ? 'bg-red-400 text-white' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {index + 1}
                            </span>
                            <div className="flex-1">
                              <div className="flex items-center justify-between text-sm">
                                <span className="font-medium">{parish}</span>
                                <span className="text-red-600 font-bold">{count}</span>
                              </div>
                              <div className="h-1.5 bg-gray-100 rounded-full mt-1 overflow-hidden">
                                <div
                                  className="h-full bg-gradient-to-r from-red-400 to-red-600 rounded-full transition-all"
                                  style={{ width: `${(count / maxParishFatalities) * 100}%` }}
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>

                  {/* Vehicle Types */}
                  <div className="bg-white rounded-2xl shadow-lg p-4">
                    <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <Car className="w-5 h-5 text-red-600" />
                      By Vehicle Type
                    </h3>
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(stats?.byVehicleType || {})
                        .sort(([, a], [, b]) => b - a)
                        .map(([type, count]) => {
                          const config = VEHICLE_TYPES[type] || { icon: Car, label: type, color: 'gray' };
                          const Icon = config.icon;
                          return (
                            <div key={type} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                              <Icon className="w-4 h-4 text-gray-600" />
                              <div className="flex-1 min-w-0">
                                <div className="text-xs text-gray-500 truncate">{config.label}</div>
                                <div className="font-bold text-sm">{count}</div>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>

                  {/* Recent Incidents */}
                  <div className="bg-white rounded-2xl shadow-lg p-4">
                    <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <Clock className="w-5 h-5 text-red-600" />
                      Recent Incidents
                    </h3>
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {accidents.slice(0, 5).map(accident => (
                        <button
                          key={accident.id}
                          onClick={() => setSelectedAccident(accident)}
                          className="w-full text-left p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-gray-900 truncate">
                                {accident.location_description || accident.city || accident.parish}
                              </div>
                              <div className="text-xs text-gray-500 mt-0.5">
                                {formatJamaicaDateShort(accident.accident_date)} • {accident.parish}
                              </div>
                            </div>
                            <div className="flex items-center gap-1 text-red-600">
                              <Skull className="w-4 h-4" />
                              <span className="font-bold">{accident.fatality_count}</span>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* List View */}
            {view === 'list' && (
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <div className="p-4 border-b bg-gray-50">
                  <h3 className="font-bold text-gray-900">
                    {accidents.length} Fatal Incidents
                    {filters.year && ` in ${filters.year}`}
                    {filters.parish && ` • ${filters.parish}`}
                  </h3>
                </div>
                <div className="divide-y">
                  {accidents.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                      <AlertCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No incidents found matching your filters.</p>
                    </div>
                  ) : (
                    accidents.map(accident => (
                      <div
                        key={accident.id}
                        className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => setSelectedAccident(accident)}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-sm font-semibold text-gray-900">
                                {accident.location_description || accident.city || 'Unknown Location'}
                              </span>
                              {accident.is_verified && (
                                <CheckCircle className="w-4 h-4 text-emerald-500" />
                              )}
                            </div>
                            <div className="flex items-center gap-3 text-xs text-gray-500">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {formatJamaicaDateShort(accident.accident_date)}
                              </span>
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {accident.parish}
                              </span>
                              {accident.road_name && (
                                <span>{accident.road_name}</span>
                              )}
                            </div>
                            {accident.summary && (
                              <p className="text-sm text-gray-600 mt-2 line-clamp-2">
                                {accident.summary}
                              </p>
                            )}
                            {/* Vehicle types involved */}
                            <div className="flex items-center gap-2 mt-2">
                              {(accident.vehicles_involved || []).map(type => {
                                const config = VEHICLE_TYPES[type] || { icon: Car, label: type };
                                const Icon = config.icon;
                                return (
                                  <span key={type} className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 rounded text-xs">
                                    <Icon className="w-3 h-3" />
                                    {config.label}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="flex items-center gap-1 text-red-600">
                              <Skull className="w-5 h-5" />
                              <span className="text-xl font-bold">{accident.fatality_count}</span>
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {accident.victims?.length || accident.fatality_count} victim(s)
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Stats View */}
            {view === 'stats' && stats && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Trend Chart */}
                <div className="bg-white rounded-2xl shadow-lg p-4 lg:col-span-2">
                  <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-red-600" />
                    Fatalities Over Time
                  </h3>
                  <div className="h-64">
                    <canvas ref={chartRef} />
                  </div>
                </div>

                {/* By Parish */}
                <div className="bg-white rounded-2xl shadow-lg p-4">
                  <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-red-600" />
                    By Parish
                  </h3>
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {Object.entries(stats.byParish)
                      .sort(([, a], [, b]) => b - a)
                      .map(([parish, count]) => (
                        <div key={parish} className="flex items-center gap-3">
                          <div className="flex-1">
                            <div className="flex items-center justify-between text-sm mb-1">
                              <span>{parish}</span>
                              <span className="font-bold text-red-600">{count}</span>
                            </div>
                            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-gradient-to-r from-red-400 to-red-600 rounded-full"
                                style={{ width: `${(count / maxParishFatalities) * 100}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>

                {/* By Victim Demographics */}
                <div className="bg-white rounded-2xl shadow-lg p-4">
                  <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Users className="w-5 h-5 text-red-600" />
                    Victim Demographics
                  </h3>

                  {/* By Age */}
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-500 mb-2">By Age Group</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(stats.byVictimAge || {}).map(([group, count]) => {
                        const config = AGE_GROUPS[group] || { label: group, color: 'gray' };
                        return (
                          <div key={group} className={`p-2 bg-${config.color}-50 rounded-lg border border-${config.color}-100`}>
                            <div className="text-lg font-bold text-gray-900">{count}</div>
                            <div className="text-xs text-gray-500">{config.label}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* By Gender */}
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-500 mb-2">By Gender</h4>
                    <div className="flex gap-4">
                      {Object.entries(stats.byVictimGender || {}).map(([gender, count]) => (
                        <div key={gender} className="flex-1 text-center p-3 bg-gray-50 rounded-lg">
                          <div className="text-2xl font-bold text-gray-900">{count}</div>
                          <div className="text-xs text-gray-500 capitalize">{gender}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* By Role */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-2">By Role</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(stats.byVictimRole || {}).map(([role, count]) => (
                        <div key={role} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                          <span className="text-sm text-gray-600">{VICTIM_ROLES[role] || role}</span>
                          <span className="font-bold text-red-600">{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Submit View */}
            {view === 'submit' && (
              <div className="max-w-2xl mx-auto">
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-3 bg-red-100 rounded-xl">
                      <FileText className="w-6 h-6 text-red-600" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">Submit Accident Report</h2>
                      <p className="text-sm text-gray-500">Help track road fatalities by submitting verified reports</p>
                    </div>
                  </div>

                  {!isAuthenticated ? (
                    <div className="text-center py-8">
                      <Shield className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p className="text-gray-600 mb-4">Please log in to submit reports</p>
                      <button
                        onClick={() => setPage?.('login')}
                        className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                      >
                        Log In
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      {/* Source Information */}
                      <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                        <h3 className="font-medium text-blue-900 mb-3 flex items-center gap-2">
                          <Newspaper className="w-5 h-5" />
                          Source Information (Required)
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div className="md:col-span-2">
                            <label className="block text-xs font-medium text-gray-600 mb-1">Article URL *</label>
                            <input
                              type="url"
                              required
                              value={submitForm.source_url}
                              onChange={(e) => setSubmitForm(f => ({ ...f, source_url: e.target.value }))}
                              placeholder="https://jamaica-gleaner.com/article/..."
                              className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-red-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Source Name</label>
                            <select
                              value={submitForm.source_name}
                              onChange={(e) => setSubmitForm(f => ({ ...f, source_name: e.target.value }))}
                              className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-red-500"
                            >
                              <option value="">Select source...</option>
                              <option value="Jamaica Gleaner">Jamaica Gleaner</option>
                              <option value="Jamaica Observer">Jamaica Observer</option>
                              <option value="Revo News">Revo News</option>
                              <option value="Loop Jamaica">Loop Jamaica</option>
                              <option value="TVJ News">TVJ News</option>
                              <option value="CVM TV">CVM TV</option>
                              <option value="Other">Other</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      {/* Accident Details */}
                      <div>
                        <h3 className="font-medium text-gray-900 mb-3">Accident Details</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Date *</label>
                            <input
                              type="date"
                              required
                              value={submitForm.accident_date}
                              onChange={(e) => setSubmitForm(f => ({ ...f, accident_date: e.target.value }))}
                              className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-red-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Time (if known)</label>
                            <input
                              type="time"
                              value={submitForm.accident_time}
                              onChange={(e) => setSubmitForm(f => ({ ...f, accident_time: e.target.value }))}
                              className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-red-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Parish *</label>
                            <select
                              required
                              value={submitForm.parish}
                              onChange={(e) => setSubmitForm(f => ({ ...f, parish: e.target.value }))}
                              className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-red-500"
                            >
                              <option value="">Select parish...</option>
                              {PARISHES.map(p => (
                                <option key={p} value={p}>{p}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">City/Town</label>
                            <input
                              type="text"
                              value={submitForm.city}
                              onChange={(e) => setSubmitForm(f => ({ ...f, city: e.target.value }))}
                              placeholder="e.g., Spanish Town"
                              className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-red-500"
                            />
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-xs font-medium text-gray-600 mb-1">Location Description *</label>
                            <input
                              type="text"
                              required
                              value={submitForm.location_description}
                              onChange={(e) => setSubmitForm(f => ({ ...f, location_description: e.target.value }))}
                              placeholder="e.g., Near the Spanish Town Hospital, along the main road"
                              className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-red-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Road Name</label>
                            <input
                              type="text"
                              value={submitForm.road_name}
                              onChange={(e) => setSubmitForm(f => ({ ...f, road_name: e.target.value }))}
                              placeholder="e.g., Mandela Highway"
                              className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-red-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Accident Type</label>
                            <select
                              value={submitForm.accident_type}
                              onChange={(e) => setSubmitForm(f => ({ ...f, accident_type: e.target.value }))}
                              className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-red-500"
                            >
                              <option value="collision">Collision</option>
                              <option value="hit_and_run">Hit and Run</option>
                              <option value="overturned">Overturned</option>
                              <option value="pedestrian_struck">Pedestrian Struck</option>
                              <option value="motorcycle_crash">Motorcycle Crash</option>
                              <option value="other">Other</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      {/* Vehicles Involved */}
                      <div>
                        <h3 className="font-medium text-gray-900 mb-3">Vehicles Involved</h3>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(VEHICLE_TYPES).map(([type, config]) => {
                            const Icon = config.icon;
                            const isSelected = submitForm.vehicles_involved.includes(type);
                            return (
                              <button
                                key={type}
                                type="button"
                                onClick={() => toggleVehicle(type)}
                                className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                                  isSelected
                                    ? 'bg-red-50 border-red-300 text-red-700'
                                    : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                                }`}
                              >
                                <Icon className="w-4 h-4" />
                                <span className="text-sm">{config.label}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Casualties */}
                      <div>
                        <h3 className="font-medium text-gray-900 mb-3">Casualties</h3>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Fatalities *</label>
                            <input
                              type="number"
                              required
                              min="1"
                              value={submitForm.fatality_count}
                              onChange={(e) => setSubmitForm(f => ({ ...f, fatality_count: parseInt(e.target.value) || 1 }))}
                              className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-red-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Injuries</label>
                            <input
                              type="number"
                              min="0"
                              value={submitForm.injury_count}
                              onChange={(e) => setSubmitForm(f => ({ ...f, injury_count: parseInt(e.target.value) || 0 }))}
                              className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-red-500"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Victims */}
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="font-medium text-gray-900">Victim Information</h3>
                          <button
                            type="button"
                            onClick={addVictim}
                            className="flex items-center gap-1 text-sm text-red-600 hover:text-red-700"
                          >
                            <Plus className="w-4 h-4" />
                            Add Victim
                          </button>
                        </div>
                        <div className="space-y-3">
                          {submitForm.victims.map((victim, index) => (
                            <div key={index} className="p-3 bg-gray-50 rounded-lg">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-gray-700">Victim {index + 1}</span>
                                {submitForm.victims.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => removeVictim(index)}
                                    className="text-red-500 hover:text-red-600"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                <div>
                                  <label className="block text-xs text-gray-500 mb-1">Age</label>
                                  <input
                                    type="number"
                                    min="0"
                                    max="120"
                                    value={victim.age}
                                    onChange={(e) => updateVictim(index, 'age', e.target.value)}
                                    placeholder="Age"
                                    className="w-full px-2 py-1.5 border rounded text-sm"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-500 mb-1">Gender</label>
                                  <select
                                    value={victim.gender}
                                    onChange={(e) => updateVictim(index, 'gender', e.target.value)}
                                    className="w-full px-2 py-1.5 border rounded text-sm"
                                  >
                                    <option value="">Unknown</option>
                                    <option value="male">Male</option>
                                    <option value="female">Female</option>
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-500 mb-1">Role</label>
                                  <select
                                    value={victim.role}
                                    onChange={(e) => updateVictim(index, 'role', e.target.value)}
                                    className="w-full px-2 py-1.5 border rounded text-sm"
                                  >
                                    {Object.entries(VICTIM_ROLES).map(([key, label]) => (
                                      <option key={key} value={key}>{label}</option>
                                    ))}
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-500 mb-1">Parish</label>
                                  <select
                                    value={victim.parish_of_origin}
                                    onChange={(e) => updateVictim(index, 'parish_of_origin', e.target.value)}
                                    className="w-full px-2 py-1.5 border rounded text-sm"
                                  >
                                    <option value="">Unknown</option>
                                    {PARISHES.map(p => (
                                      <option key={p} value={p}>{p}</option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Summary */}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Summary / Notes</label>
                        <textarea
                          rows={3}
                          value={submitForm.summary}
                          onChange={(e) => setSubmitForm(f => ({ ...f, summary: e.target.value }))}
                          placeholder="Brief summary of the incident as reported..."
                          className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-red-500"
                        />
                      </div>

                      {/* Submit Button */}
                      <button
                        type="submit"
                        disabled={submitting}
                        className="w-full py-3 bg-red-600 text-white font-medium rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {submitting ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Submitting...
                          </>
                        ) : (
                          <>
                            <Send className="w-5 h-5" />
                            Submit Report for Review
                          </>
                        )}
                      </button>

                      <p className="text-xs text-gray-500 text-center">
                        All submissions are reviewed by administrators before being added to the tracker.
                        Thank you for helping improve road safety awareness in Jamaica.
                      </p>
                    </form>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Accident Detail Modal */}
      {selectedAccident && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setSelectedAccident(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-red-100 rounded-xl">
                    <AlertTriangle className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">Fatal Incident</h2>
                    <p className="text-sm text-gray-500">{selectedAccident.parish}</p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedAccident(null)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Fatality Count */}
                <div className="flex items-center justify-center p-4 bg-red-50 rounded-xl">
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-2 text-red-600 mb-1">
                      <Skull className="w-8 h-8" />
                      <span className="text-4xl font-bold">{selectedAccident.fatality_count}</span>
                    </div>
                    <div className="text-sm text-red-700">
                      {selectedAccident.fatality_count === 1 ? 'Life Lost' : 'Lives Lost'}
                    </div>
                  </div>
                </div>

                {/* Details */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="text-xs text-gray-500 mb-1">Date</div>
                    <div className="font-medium">
                      {formatJamaicaDate(selectedAccident.accident_date)}
                    </div>
                  </div>
                  {selectedAccident.accident_time && (
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <div className="text-xs text-gray-500 mb-1">Time</div>
                      <div className="font-medium">{formatTime(selectedAccident.accident_time)}</div>
                    </div>
                  )}
                </div>

                {/* Location */}
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="text-xs text-gray-500 mb-1">Location</div>
                  <div className="font-medium">{selectedAccident.location_description || 'Unknown'}</div>
                  {selectedAccident.road_name && (
                    <div className="text-sm text-gray-600">{selectedAccident.road_name}</div>
                  )}
                  <div className="text-sm text-gray-500">{selectedAccident.city ? `${selectedAccident.city}, ` : ''}{selectedAccident.parish}</div>
                </div>

                {/* Vehicles */}
                {selectedAccident.vehicles_involved?.length > 0 && (
                  <div>
                    <div className="text-xs text-gray-500 mb-2">Vehicles Involved</div>
                    <div className="flex flex-wrap gap-2">
                      {selectedAccident.vehicles_involved.map(type => {
                        const config = VEHICLE_TYPES[type] || { icon: Car, label: type };
                        const Icon = config.icon;
                        return (
                          <span key={type} className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 rounded-full text-sm">
                            <Icon className="w-4 h-4" />
                            {config.label}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Victims */}
                {selectedAccident.victims?.length > 0 && (
                  <div>
                    <div className="text-xs text-gray-500 mb-2">Victims ({selectedAccident.victims.length})</div>
                    <div className="space-y-2">
                      {selectedAccident.victims.map((victim, i) => (
                        <div key={i} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                          <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                            <User className="w-4 h-4 text-gray-500" />
                          </div>
                          <div className="flex-1">
                            <div className="text-sm font-medium">
                              {victim.age ? `${victim.age} years old` : 'Age unknown'}
                              {victim.gender && `, ${victim.gender}`}
                            </div>
                            <div className="text-xs text-gray-500">
                              {VICTIM_ROLES[victim.role] || victim.role}
                              {victim.parish_of_origin && ` • From ${victim.parish_of_origin}`}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Summary */}
                {selectedAccident.summary && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="text-xs text-gray-500 mb-1">Summary</div>
                    <div className="text-sm text-gray-700">{selectedAccident.summary}</div>
                  </div>
                )}

                {/* Source */}
                {selectedAccident.source_url && (
                  <a
                    href={selectedAccident.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <ExternalLink className="w-4 h-4 text-blue-600" />
                      <span className="text-sm text-blue-700">
                        {selectedAccident.source_name || 'View Source'}
                      </span>
                    </div>
                    <span className="text-xs text-blue-500">Opens in new tab</span>
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Safety Tips Footer */}
      <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white p-6 mt-8 rounded-2xl shadow-xl">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-8 h-8" />
            <h2 className="text-xl font-bold">Road Safety Tips</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-white/10 rounded-xl">
              <h3 className="font-semibold mb-2">For Drivers</h3>
              <ul className="text-sm text-emerald-100 space-y-1">
                <li>• Always wear your seatbelt</li>
                <li>• Never drink and drive</li>
                <li>• Obey speed limits</li>
                <li>• Avoid using phones while driving</li>
              </ul>
            </div>
            <div className="p-4 bg-white/10 rounded-xl">
              <h3 className="font-semibold mb-2">For Pedestrians</h3>
              <ul className="text-sm text-emerald-100 space-y-1">
                <li>• Use pedestrian crossings</li>
                <li>• Look both ways before crossing</li>
                <li>• Wear bright colors at night</li>
                <li>• Walk facing traffic on roads</li>
              </ul>
            </div>
            <div className="p-4 bg-white/10 rounded-xl">
              <h3 className="font-semibold mb-2">For Motorcyclists</h3>
              <ul className="text-sm text-emerald-100 space-y-1">
                <li>• Always wear a helmet</li>
                <li>• Use reflective gear at night</li>
                <li>• Avoid weaving through traffic</li>
                <li>• Be extra cautious in rain</li>
              </ul>
            </div>
          </div>
          <div className="mt-4 text-center text-sm text-emerald-100">
            For more road safety information, visit the{' '}
            <a
              href="http://www.nationalroadsafetycouncil.org.jm/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-white"
            >
              National Road Safety Council
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoadFatalityTracker;
